import { useCallback, useEffect, useRef, useState } from "react";

/* Canonical Academy account progress — same API contract as Virtue Guide mobile.
   Keys: scenarioPrompt, mentorQuestion, reflection:{i}, journalPrompt,
   practiceAssignment (responses) and exercise:{i} (exercise_results). */

const API = process.env.REACT_APP_BACKEND_URL;

async function req(method, path, body) {
  const res = await fetch(`${API}/api/academy${path}`, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => null);
    throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, detail });
  }
  return res.json();
}

export const fetchAccountProgress = () => req("GET", "/progress");

/* Legacy-shaped records for the course/phase overviews (StatusBadge,
   PhaseProgress, ResumeButton) — sourced from the canonical account state. */
export async function fetchTrackRecords(trackId) {
  try {
    const d = await fetchAccountProgress();
    const lessons = d?.tracks?.[trackId]?.lessons || {};
    return Object.entries(lessons).map(([slug, p]) => ({
      lessonSlug: slug,
      learningStatus: p.status === "completed" ? "Applied" : "Encountered",
      workStatus: p.status === "completed" ? "completed" : "draft",
      lastActivityAt: p.updated_at || null,
      completedAt: p.completed_at || null,
    }));
  } catch {
    return [];
  }
}

export function useLessonProgress(trackId, lessonSlug) {
  const [progress, setProgress] = useState(null);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved | failed
  const pendingR = useRef({});
  const pendingE = useRef({});
  const revision = useRef(0);
  const timer = useRef(null);
  const lastSection = useRef(null);

  useEffect(() => {
    let dead = false;
    setProgress(null);
    setSaveState("idle");
    pendingR.current = {};
    pendingE.current = {};
    lastSection.current = null;
    req("GET", `/tracks/${trackId}/lessons/${lessonSlug}/progress`)
      .then((p) => {
        if (dead) return;
        revision.current = p.revision || 0;
        setProgress(p);
      })
      .catch(() => {
        if (!dead) setProgress({ status: null, responses: {}, exercise_results: {}, unavailable: true });
      });
    return () => {
      dead = true;
      clearTimeout(timer.current);
    };
  }, [trackId, lessonSlug]);

  const adopt = useCallback((fresh) => {
    revision.current = fresh.revision || 0;
    setProgress(() => ({
      ...fresh,
      responses: { ...(fresh.responses || {}), ...pendingR.current },
      exercise_results: { ...(fresh.exercise_results || {}), ...pendingE.current },
    }));
  }, []);

  const flush = useCallback(async (retried = false) => {
    clearTimeout(timer.current);
    const responses = { ...pendingR.current };
    const results = { ...pendingE.current };
    pendingR.current = {};
    pendingE.current = {};
    if (!Object.keys(responses).length && !Object.keys(results).length) return true;
    setSaveState("saving");
    const body = { base_revision: revision.current };
    if (Object.keys(responses).length) body.responses = responses;
    if (Object.keys(results).length) body.exercise_results = results;
    if (lastSection.current) body.last_section_id = lastSection.current;
    try {
      const fresh = await req("PATCH", `/tracks/${trackId}/lessons/${lessonSlug}/progress`, body);
      adopt(fresh);
      setSaveState("saved");
      return true;
    } catch (e) {
      // never discard typed content: requeue (newer local edits win)
      pendingR.current = { ...responses, ...pendingR.current };
      pendingE.current = { ...results, ...pendingE.current };
      const stale = e.status === 409 ? e.detail?.detail : null;
      if (stale && !retried) {
        revision.current = stale.server_revision || 0;
        return flush(true);
      }
      setSaveState("failed");
      return false;
    }
  }, [trackId, lessonSlug, adopt]);

  const queue = useCallback((kind, key, value) => {
    lastSection.current = key;
    if (kind === "r") {
      pendingR.current[key] = value;
      setProgress((p) => ({ ...(p || {}), responses: { ...(p?.responses || {}), [key]: value } }));
    } else {
      pendingE.current[key] = value;
      setProgress((p) => ({ ...(p || {}), exercise_results: { ...(p?.exercise_results || {}), [key]: value } }));
    }
    setSaveState("saving");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(), 900);
  }, [flush]);

  const setResponse = useCallback((key, value) => queue("r", key, value), [queue]);
  const setExerciseResult = useCallback((key, value) => queue("e", key, value), [queue]);

  const complete = useCallback(async () => {
    clearTimeout(timer.current);
    await flush();
    setSaveState("saving");
    try {
      const fresh = await req("POST", `/tracks/${trackId}/lessons/${lessonSlug}/complete`);
      adopt(fresh);
      setSaveState("saved");
      return true;
    } catch {
      setSaveState("failed");
      return false;
    }
  }, [trackId, lessonSlug, flush, adopt]);

  return {
    progress,
    loaded: progress !== null,
    responses: progress?.responses || {},
    exerciseResults: progress?.exercise_results || {},
    setResponse,
    setExerciseResult,
    flush,
    complete,
    saveState,
    completed: progress?.status === "completed",
    lastSavedAt: progress?.updated_at || null,
  };
}
